import { useState, useEffect, useCallback } from 'react';
import { EmailTemplate } from '../types';
import { templatesService } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useQuota } from '../context/QuotaContext';
import { useOptimisticCRUD, createOptimisticItem } from './useOptimisticCRUD';

export interface UseTemplatesOptions {
  /** Optional: called when an update fails (e.g. show toast) */
  onUpdateFailed?: () => void;
}

export function useTemplates(options: UseTemplatesOptions = {}) {
  const { onUpdateFailed } = options;
  const { user, loading: authLoading } = useAuth();
  const { showQuotaLimit } = useQuota();

  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const next = await templatesService.getAll();
      setTemplates(next);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Failed to load templates');
    }
  }, []);

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }
    if (!user?.id) {
      setLoading(false);
      setTemplates([]);
      setError(null);
      return;
    }
    setError(null);
    setLoading(true);
    templatesService
      .getAll()
      .then(setTemplates)
      .catch((err) => {
        console.error('Error loading templates:', err);
        setError('Failed to load templates');
      })
      .finally(() => setLoading(false));
  }, [user?.id, authLoading]);

  const addItem = useCallback((item: EmailTemplate) => {
    setTemplates((prev) => {
      if (prev.some((t) => t.id === item.id)) return prev;
      return [item, ...prev];
    });
  }, []);
  const updateItem = useCallback((item: EmailTemplate) => {
    setTemplates((prev) => prev.map((t) => (t.id === item.id ? item : t)));
  }, []);
  const removeItem = useCallback((id: string) => {
    setTemplates((prev) => prev.filter((t) => t.id !== id));
  }, []);
  const replaceItem = useCallback((tempId: string, realItem: EmailTemplate) => {
    setTemplates((prev) => prev.map((t) => (t.id === tempId ? realItem : t)));
  }, []);

  const crud = useOptimisticCRUD<EmailTemplate, Omit<EmailTemplate, 'id'>>({
    onOptimisticAdd: addItem,
    onOptimisticUpdate: updateItem,
    onOptimisticRemove: removeItem,
    onOptimisticReplace: replaceItem,
    service: {
      create: async (data) => {
        const result = (await templatesService.create(data)) as any;
        if (result?.error === 'QUOTA_LIMIT') {
          showQuotaLimit(result.quotaInfo);
          return null;
        }
        return result && !result.error ? result : null;
      },
      update: (id, data) => templatesService.update(id, data as EmailTemplate),
      delete: (id) => templatesService.delete(id),
    },
    onRollback: refresh,
    onUpdateFailed,
  });

  const insertTemplate = useCallback(
    (template: EmailTemplate) => {
      addItem(template);
    },
    [addItem]
  );

  const addTemplate = useCallback(
    (template: Omit<EmailTemplate, 'id' | 'createdAt' | 'updatedAt'>) => {
      const optimistic = createOptimisticItem<EmailTemplate>(template);
      crud.create(template as Omit<EmailTemplate, 'id'>, optimistic);
    },
    [crud]
  );

  const updateTemplate = useCallback(
    (template: EmailTemplate, updateOptions?: { previousItem?: EmailTemplate }) => {
      crud.update(template.id, { ...template, updatedAt: new Date() }, updateOptions);
    },
    [crud]
  );

  const deleteTemplate = useCallback(
    (id: string) => {
      crud.remove(id);
    },
    [crud]
  );

  return {
    templates,
    loading,
    error,
    refresh,
    addTemplate,
    updateTemplate,
    deleteTemplate,
    insertTemplate,
  };
}
